import { z } from 'zod';

import { ENGLISH_TITLES } from './english-titles';
import { THAI_TITLES } from './thai-titles';

export { THAI_TITLES } from './thai-titles';
export { ENGLISH_TITLES } from './english-titles';

/**
 * Centralized title definitions for the monorepo.
 *
 * Both lists come from the client master data (see `./thai-titles.ts` and
 * `./english-titles.ts`, hand-maintained). The same lists drive the
 * backend's accepted-title validation in set-api-monorepo — keep the files
 * in sync.
 *
 * Ordering of {@link THAI_TITLES} and {@link ENGLISH_TITLES} is the
 * authoritative display order used by dropdowns across the app.
 */

export type ThaiTitle = (typeof THAI_TITLES)[number];

export type EnglishTitle = (typeof ENGLISH_TITLES)[number];

/**
 * The 5 English titles offered to foreign examinees (passport-based
 * registration flows). A fixed subset of {@link ENGLISH_TITLES} — the full
 * master-data list is too large for that flow's dropdown and the examinee
 * only self-identifies at this granularity. Proctors and the blocklist use
 * the full {@link ENGLISH_TITLES} list instead.
 */
export const EXAMINEE_ENGLISH_TITLES = ['Mr.', 'Mrs.', 'Miss', 'Master', 'Ms.'] as const;

export type ExamineeEnglishTitle = (typeof EXAMINEE_ENGLISH_TITLES)[number];

/**
 * Combined list of all titles (Thai + English) in the canonical display
 * order. Useful for forms that accept both Thai and English titles in a
 * single dropdown.
 */
export const ALL_TITLES = [...THAI_TITLES, ...ENGLISH_TITLES] as const;

export type AllTitle = (typeof ALL_TITLES)[number];

export function isThaiTitle(value: string): value is ThaiTitle {
  return THAI_TITLES.includes(value as ThaiTitle);
}

export function isEnglishTitle(value: string): value is EnglishTitle {
  return ENGLISH_TITLES.includes(value as EnglishTitle);
}

export function isExamineeEnglishTitle(value: string): value is ExamineeEnglishTitle {
  return EXAMINEE_ENGLISH_TITLES.includes(value as ExamineeEnglishTitle);
}

/**
 * Shape of a selection option used by this module. Structurally compatible
 * with `OptionSelectionProps` from `@shared/components` so consumers can pass
 * these directly to selection components without any casting.
 *
 * Defined locally to avoid creating a `@shared/utils` -> `@shared/components`
 * dependency cycle (components already depends on utils).
 */
export interface TitleSelectionOption {
  label: string;
  value: string;
}

/**
 * Selection options for the full Thai title list (label === value).
 */
export const THAI_TITLE_OPTIONS: TitleSelectionOption[] = THAI_TITLES.map((title) => ({
  label: title,
  value: title,
}));

/**
 * Selection options for the full English title list (label === value).
 */
export const ENGLISH_TITLE_OPTIONS: TitleSelectionOption[] = ENGLISH_TITLES.map((title) => ({
  label: title,
  value: title,
}));

/**
 * Selection options for the fixed 5-title examinee English list (label === value).
 */
export const EXAMINEE_ENGLISH_TITLE_OPTIONS: TitleSelectionOption[] = EXAMINEE_ENGLISH_TITLES.map(
  (title) => ({
    label: title,
    value: title,
  }),
);

/**
 * Selection options for all titles (Thai + English) (label === value).
 */
export const ALL_TITLE_OPTIONS: TitleSelectionOption[] = ALL_TITLES.map((title) => ({
  label: title,
  value: title,
}));

/**
 * Shared validation message for a missing/invalid title selection.
 */
export const TITLE_REQUIRED_ERROR_MESSAGE = 'กรุณาเลือกคำนำหน้า';

/**
 * Zod schema for a `{ value, label }` selection option whose value must be a
 * valid Thai title. Use in form schemas instead of a plain
 * `z.object({ value: z.string(), label: z.string() })` so membership in the
 * central list is enforced.
 */
export const thaiTitleOptionSchema = z.object(
  { value: z.enum(THAI_TITLES), label: z.string() },
  { message: TITLE_REQUIRED_ERROR_MESSAGE },
);

/**
 * Zod schema for a `{ value, label }` selection option whose value must be a
 * valid English title.
 */
export const englishTitleOptionSchema = z.object(
  { value: z.enum(ENGLISH_TITLES), label: z.string() },
  { message: TITLE_REQUIRED_ERROR_MESSAGE },
);

/**
 * Zod schema for a `{ value, label }` selection option whose value must be
 * one of the 5 examinee English titles.
 */
export const examineeEnglishTitleOptionSchema = z.object(
  { value: z.enum(EXAMINEE_ENGLISH_TITLES), label: z.string() },
  { message: TITLE_REQUIRED_ERROR_MESSAGE },
);
